import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DeviceGateway } from "@/contracts";
import { useTranslation } from "react-i18next";

import { gatewayOptionsQuery, linkGatewaysMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { FormDialog } from "@/components/ui/form-dialog";
import { LoadingBlock } from "@/components/ui/page";
import { useApiClient } from "@/lib/api-context";
import { EM_DASH, formatDevEui } from "@/lib/format";

/** The API accepts ten at a time, which is well past any real installation. */
const MAX_GATEWAYS = 10;

/**
 * Picks the gateways that should carry this device.
 *
 * Several rather than one, because a sensor's transmission is heard by every
 * gateway in range and configuring two is how an installation survives one of
 * them failing. Choosing two does not make the device redundant by itself —
 * that is only true once both have been heard through, which this dialog
 * cannot promise and the card behind it reports honestly.
 */
export function LinkGatewaysDialog({
  deviceId,
  open,
  configured,
  onClose,
}: {
  deviceId: string;
  open: boolean;
  configured: DeviceGateway[];
  onClose: () => void;
}) {
  const { t } = useTranslation("pages");
  const client = useApiClient();
  const queryClient = useQueryClient();

  const gateways = useQuery({ ...gatewayOptionsQuery(client), enabled: open });

  /**
   * Only what the user is adding.
   *
   * Seeding this from the already-configured gateways would mean holding a
   * copy of server state in a component, and then reconciling the two. The
   * ticks for existing coverage are derived below instead, which leaves one
   * meaning per value and no effect to synchronize them.
   */
  const [additions, setAdditions] = useState<string[]>([]);

  const alreadyLinked = configured.filter((entry) => !entry.shared).map((entry) => entry.gatewayId);

  const mutation = useMutation({
    ...linkGatewaysMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["devices", "installations", deviceId] });
      await queryClient.invalidateQueries({ queryKey: ["gateways"] });
    },
  });

  function close() {
    setAdditions([]);
    mutation.reset();
    onClose();
  }

  function toggle(id: string) {
    setAdditions((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  const onSubmit = async () => {
    if (additions.length === 0) {
      close();

      return;
    }

    try {
      await mutation.mutateAsync({ deviceId, body: { gatewayIds: additions } });
      close();
    } catch {
      // The alert below reports it; the dialog stays open so the selection is
      // not lost.
    }
  };

  const options = gateways.data?.data ?? [];

  return (
    <FormDialog
      open={open}
      title={t("configureGatewaysTitle")}
      description={t("dialogLinkGatewaysDescription")}
      submitLabel={
        additions.length > 1
          ? t("configureGateways", { count: additions.length })
          : t("configure")
      }
      pending={mutation.isPending}
      onSubmit={(event) => {
        event.preventDefault();
        void onSubmit();
      }}
      onCancel={close}
    >
      <div className="flex flex-col gap-4">
        {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

        {additions.length > MAX_GATEWAYS ? (
          <Alert>Choose at most {MAX_GATEWAYS} gateways at a time.</Alert>
        ) : null}

        {gateways.isPending ? <LoadingBlock label={t("loadingGateways")} /> : null}

        {!gateways.isPending && options.length === 0 ? (
          <p className="text-sm text-ink-muted">
            No gateway is registered yet. Register one first {EM_DASH} a sensor with nothing in
            range of it cannot report, and no setting here changes that.
          </p>
        ) : null}

        {options.length > 0 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 text-sm font-medium text-ink">{t("legendAvailableGateways")}</legend>

            {options.map((gateway) => {
              const linked = alreadyLinked.includes(gateway.id);

              return (
                <label
                  key={gateway.id}
                  className="flex items-start gap-3 rounded-md border border-line px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={linked || additions.includes(gateway.id)}
                    disabled={linked}
                    onChange={() => toggle(gateway.id)}
                  />

                  <span className="flex flex-col">
                    <span className="font-medium text-ink">{gateway.name}</span>
                    <span className="font-mono text-xs text-ink-muted">
                      {formatDevEui(gateway.eui)}
                      {gateway.siteName ? ` · ${gateway.siteName}` : ""}
                    </span>
                    {linked ? (
                      <span className="text-xs text-ink-muted">
                        Already configured. Remove it from the installation to undo that.
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </fieldset>
        ) : null}

        {configured.some((entry) => entry.shared) ? (
          <Alert tone="info">
            This device is also covered by platform infrastructure, which is not listed here because
            it is not yours to configure.
          </Alert>
        ) : null}
      </div>
    </FormDialog>
  );
}
